import React from "react";
import { useNavigate } from "react-router-dom";
import { getAvatarUrl } from "../../utils/avatar";
import ro from "/src/content/ro.json";

const AdminSidebar = ({
  drawerOpen,
  adminUser,
  exportInscrieriCSV,
  abonamente,
  formatDate,
  selectedTraseu,
  setSelectedTraseu,
  nextTraining,
  totalPersoane,
}) => {
  const navigate = useNavigate();
  const avatarUrl = getAvatarUrl({
    photoURL: adminUser?.photoURL || adminUser?.providerData?.[0]?.photoURL,
    email: adminUser?.email,
    name: adminUser?.displayName,
  });
  const displayName = adminUser?.displayName || ro.admin.sidebar.defaultName;
  const displayEmail = adminUser?.email || ro.admin.sidebar.defaultEmail;
  const navItems = ro.admin.sidebar.navItems;

  return (
    <aside
      className={`admin-sidebar fixed left-0 top-0 h-screen w-72 flex flex-col shadow-2xl transition-transform duration-300 z-50 ${
        drawerOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      }`}
    >
      {/* ================= CONTAINER CONT ================= */}
      <div
        className="px-4 pt-4 pb-3"
        style={{ borderBottom: "1px solid var(--admin-sidebar-border)" }}
      >
        <div className="admin-sidebar-card rounded-xl px-3 py-4 text-center">
          <div className="w-16 h-16 mx-auto rounded-full overflow-hidden border-2 border-orange-500 shadow-lg flex items-center justify-center">
            <img
              src={avatarUrl}
              alt={displayName}
              className="w-full h-full object-cover"
              onError={(e) => (e.target.src = "/images/imgaine1.svg")}
            />
          </div>
          <p className="mt-2 text-base font-semibold admin-sidebar-text">
            {displayName}
          </p>
          <p className="text-[11px] admin-sidebar-muted">{displayEmail}</p>
        </div>
      </div>

      {/* ================= ZONA SCROLL ================= */}
      <div className="admin-sidebar-scroll flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {/* NAVIGARE */}
        <section className="admin-sidebar-card rounded-xl p-3">
          <p className="text-xs admin-sidebar-muted mb-2">
            {ro.admin.sidebar.navTitle}
          </p>
          <div className="space-y-1">
            {navItems.map((item, i) => {
              const navType = item.type;
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => {
                    if (navType === "users") {
                      navigate("/admin/utilizatori");
                      return;
                    }
                    if (!navType) return;
                    window.dispatchEvent(
                      new CustomEvent("adminOpenDrawer", {
                        detail: { type: navType },
                      })
                    );
                  }}
                  className={`admin-sidebar-nav-item w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${
                    i === 0 ? "is-active" : ""
                  }`}
                >
                  <span>{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* ABONAMENTE CARE EXPIRA */}
        <section className="admin-sidebar-card rounded-xl p-3">
          <p className="text-xs admin-sidebar-muted mb-2">
            {ro.admin.sidebar.subscriptionsTitle}
          </p>
          <div className="admin-sidebar-scroll-inner space-y-2 max-h-40 overflow-y-auto pr-1">
            {abonamente.length > 0 ? (
              abonamente.map((a) => (
                <div
                  key={a.id}
                  className="admin-sidebar-item p-2 rounded-lg text-xs"
                >
                  <p className="admin-sidebar-accent font-semibold">
                    {a.nume || ro.admin.sidebar.subscriptionAnon}
                  </p>
                  <p className="admin-sidebar-muted">{a.telefon || "-"}</p>
                  <p>
                    <span className="admin-sidebar-accent">
                      {ro.admin.sidebar.subscriptionExpire}
                    </span>{" "}
                    {formatDate(a.dataFinal)}
                  </p>
                  <p className="admin-sidebar-muted">
                    {a.planAlimentar || ro.admin.sidebar.subscriptionNoPlan}
                  </p>
                </div>
              ))
            ) : (
              <p className="admin-sidebar-muted text-xs">
                {ro.admin.sidebar.subscriptionEmpty}
              </p>
            )}
          </div>
        </section>

        {/* SELECTOR TRASEU */}
        <section className="admin-sidebar-card rounded-xl p-3">
          <p className="text-xs admin-sidebar-muted mb-1">
            {ro.admin.sidebar.selectTraseu}
          </p>
          <select
            value={selectedTraseu}
            onChange={(e) => setSelectedTraseu(e.target.value)}
            className="admin-sidebar-input w-full px-2 py-1 rounded-md outline-none"
          >
            <option>MAI</option>
            <option>MAPN</option>
            <option>ISU</option>
            <option>ANP</option>
          </select>
        </section>

        {/* URMATORUL ANTRENAMENT */}
        <section className="admin-sidebar-card rounded-xl p-3 text-xs">
          <p className="admin-sidebar-muted mb-1">
            {ro.admin.sidebar.nextTrainingTitle}
          </p>
          {nextTraining ? (
            <>
              <p className="admin-sidebar-accent font-semibold">
                {nextTraining.tipProgram || "-"}
              </p>
              <p className="admin-sidebar-muted">
                {formatDate(nextTraining.data)} – {nextTraining.ora || "-"}
              </p>
              <p className="admin-sidebar-muted">{nextTraining.locatie || "-"}</p>
              <p className="admin-sidebar-muted-strong">
                {ro.admin.sidebar.participantsCount} {totalPersoane}
              </p>
            </>
          ) : (
            <p className="admin-sidebar-muted">
              {ro.admin.sidebar.nextTrainingNone.replace(
                "{traseu}",
                selectedTraseu
              )}
            </p>
          )}
        </section>

        {/* ================= FOOTER ================= */}
        <div className="admin-sidebar-footer pt-4 space-y-2">
          <button
            onClick={exportInscrieriCSV}
            className="admin-sidebar-primary w-full py-2 rounded-lg transition shadow"
          >
            {ro.admin.sidebar.printCsv}
          </button>

          <button
            onClick={() =>
              navigate(
                `/admin/prezenta?tip=${encodeURIComponent(selectedTraseu || "")}`
              )
            }
            className="admin-sidebar-secondary w-full py-2 rounded-lg transition"
          >
            {ro.admin.sidebar.attendance}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;
